import { OnOffIcon } from "@/assets/images/svgIcons/onOffIcon"
import { ProLargeIcon } from "@/assets/images/svgIcons/proLargeIcon"
import { Badge } from "@/components/ui/baseUi/badge"
import { Counter } from "@/components/ui/baseUi/counter"
import { Text } from "@/components/ui/baseUi/text"
import { CurrencyFormattedPrice } from "@/modules/currency/components/currencyUtils"
import { cn } from "@/modules/lib/utils"
import type { UserProgramItemEntity } from "@/modules/programs/entities/userProgramItemEntity"
import { ProgramType, ProgramVisibility } from "@/modules/programs/types/programTypes"
import { word } from "@modules/core/utils/i18n"
import { InfinityIcon, LockIcon, LockOpenIcon, UsersRoundIcon } from "lucide-react"

type CoachOfferRemoteCardProps = {
  program: UserProgramItemEntity
} & React.ComponentProps<"div">

export function CoachOfferRemoteCard({ program, className, ...props }: CoachOfferRemoteCardProps) {
  return (
    <div
      className={cn(
        "flex w-full cursor-pointer justify-between gap-4 rounded-xl border border-surface-border-gray-default p-4",
        className,
      )}
      {...props}
    >
      <div className="flex w-7/12 flex-col justify-between gap-3">
        <div className="flex flex-col">
          <Text fontStyle="TM" className="leading-none wrap-anywhere text-textOnSurface-default">
            {program.name}
          </Text>
        </div>

        {program.visibility === ProgramVisibility.PUBLIC ? (
          <div className="flex flex-row items-center gap-1">
            <LockOpenIcon size={16} />
            <Text fontStyle="BS" className="leading-normal text-textOnSurface-default">
              {word("coach.offers.public")}
            </Text>
          </div>
        ) : (
          <div className="flex flex-row items-center gap-1">
            <LockIcon size={16} />
            <Text fontStyle="BS" className="leading-normal text-textOnSurface-default">
              {word("coach.offers.private")}
            </Text>
          </div>
        )}
      </div>

      <div className="flex flex-col items-end justify-between">
        <div className="flex flex-col items-end gap-1">
          <Badge variant="primaryIntense" size="lg">
            {getTypeIcon(program.type)}
            {getTypeLabel(program.type)}
          </Badge>
          <Badge size="md" variant="neutralSubtle">
            <UsersRoundIcon size={16} />
            {/* TODO NST : end this part */}
            <Text fontStyle="LM" className="text-textOnSurface-muted">
              ???
            </Text>
            <Counter variant="negativeSubtle" size="sm">
              ???
            </Counter>
          </Badge>
        </div>

        <RemoteOfferPrice program={program} />
      </div>
    </div>
  )
}

function getTypeIcon(type: ProgramType) {
  switch (type) {
    case ProgramType.VIRTUAL:
      return <InfinityIcon color="currentColor" width={24} height={24} className="shrink-0" />
    case ProgramType.ONOFF:
      return <OnOffIcon color="currentColor" width={24} height={24} className="shrink-0" />
    case ProgramType.PRO:
      return <ProLargeIcon color="currentColor" width={24} height={24} className="shrink-0" />
  }
}

function getTypeLabel(type: ProgramType) {
  switch (type) {
    case ProgramType.VIRTUAL:
      return word("program.create.infinite.title")
    case ProgramType.ONOFF:
      return word("program.create.on_off.title")
    case ProgramType.PRO:
      return word("program.create.pro.title")
  }
}

function RemoteOfferPrice({ program }: { program: UserProgramItemEntity }) {
  if (program.price?.value) {
    if (program.type === ProgramType.VIRTUAL || program.type === ProgramType.PRO) {
      return (
        <div className="flex flex-row items-baseline gap-2">
          <CurrencyFormattedPrice price={program.price} wholePartFontStyle="TM" decimalPartFontStyle="TS" />
          <Text fontStyle="TS">{word("program.price.pricePerMonth")}</Text>
        </div>
      )
    } else {
      return (
        <div className="flex flex-row items-baseline gap-2">
          <CurrencyFormattedPrice price={program.price} wholePartFontStyle="TM" decimalPartFontStyle="TS" />
          <Text fontStyle="TS">{word("program.price.onlyOnce")}</Text>
        </div>
      )
    }
  } else {
    return <Text fontStyle="TM">{word("program.price.free")}</Text>
  }
}
